/**
 * AddNoteDialog — append a free-text note to a meal/feed group.
 *
 * Tube feeding has a smart-merge path: if the open feed group has a food-only
 * entry (food but no comment), the new note is PATCHED onto it instead of
 * POSTing a new entry.
 */
import * as React from "react";

import { Widget, WidgetContent } from "@/components/consultation/shared/Widget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  useAddMealNote,
  useUpdateMealEntry,
} from "@/hooks/useNutritionEvaluation";
import { cn } from "@/lib/utils";
import type { FeedEntry } from "@/types/nutrition";
import { Loader2, StickyNote } from "lucide-react";

const NOTE_MAX_LENGTH = 150;

interface AddNoteDialogProps {
  open: boolean;
  onClose: () => void;
  mealGroupId: string;
  mealTimeLabel: string;
  appointmentId: string;
  isTubeFeeding: boolean;
  isMealPlan?: boolean;
  /** For tube-feeding smart merge with an existing food-only entry. */
  feedEntries?: FeedEntry[];
}

export const AddNoteDialog: React.FC<AddNoteDialogProps> = ({
  open,
  onClose,
  mealGroupId,
  mealTimeLabel,
  appointmentId,
  isTubeFeeding,
  isMealPlan = false,
  feedEntries = [],
}) => {
  const [note, setNote] = React.useState("");
  const addNote = useAddMealNote(appointmentId, isTubeFeeding, isMealPlan);
  const updateEntry = useUpdateMealEntry(
    appointmentId,
    isTubeFeeding,
    isMealPlan
  );

  const findFoodOnlyEntry = React.useCallback(() => {
    if (!isTubeFeeding) return null;
    return feedEntries.find(
      (entry) =>
        (entry.food_item_details || entry.feed_item_details) &&
        (!entry.comment || entry.comment.trim() === "")
    );
  }, [isTubeFeeding, feedEntries]);

  const handleAdd = () => {
    if (!note.trim()) return;

    if (isTubeFeeding) {
      const foodOnlyEntry = findFoodOnlyEntry();
      if (foodOnlyEntry) {
        updateEntry.mutate(
          { entryId: foodOnlyEntry.id, data: { comment: note } },
          {
            onSuccess: () => {
              onClose();
              setNote("");
            },
          }
        );
        return;
      }
    }

    addNote.mutate({ group_id: mealGroupId, comment: note });
    onClose();
    setNote("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        setNote("");
      }}
    >
      <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <StickyNote className="h-5 w-5 text-primary" />
            Add Notes
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="flex-1 overflow-y-auto p-6 bg-background space-y-4">
          <Textarea
            placeholder="Add Note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            className="resize-none"
          />
          <p
            className={cn(
              "z-caption text-right",
              note.length > NOTE_MAX_LENGTH
                ? "text-destructive font-medium"
                : "text-muted-foreground"
            )}
          >
            {NOTE_MAX_LENGTH - note.length} characters left
          </p>

          <Widget>
            <WidgetContent className="py-3">
              <div className="flex items-center gap-4">
                <span className="z-h3 text-foreground">Food Group:</span>
                <Badge variant="secondary" className="z-body">
                  {mealTimeLabel}
                </Badge>
              </div>
            </WidgetContent>
          </Widget>
        </div>

        <DialogFooter className="p-6 mt-auto border-t border-border/60 bg-background flex justify-end gap-2">
          <Button
            onClick={handleAdd}
            disabled={
              !note.trim() ||
              note.length > NOTE_MAX_LENGTH ||
              addNote.isPending ||
              updateEntry.isPending
            }
          >
            {(addNote.isPending || updateEntry.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
